import { api } from '@/lib/api';

/** The seven things the daily check compares (ALS-388), in the order the tab draws them, as the API names them. */
export const XERO_SYNC_CHECK = {
    Documents: 'Documents',
    AmountsDue: 'AmountsDue',
    Payments: 'Payments',
    Wallet: 'Wallet',
    Allocations: 'Allocations',
    XeroOnly: 'XeroOnly',
    Contacts: 'Contacts',
} as const;

export type XeroSyncCheck = (typeof XERO_SYNC_CHECK)[keyof typeof XERO_SYNC_CHECK];

export const XERO_SYNC_CHECKS = Object.values(XERO_SYNC_CHECK) as [
    XeroSyncCheck,
    ...XeroSyncCheck[],
];

/** What one finding says differs, as the API names it. */
export const XERO_SYNC_KIND = {
    NotPushed: 'NotPushed',
    Missing: 'Missing',
    Voided: 'Voided',
    TotalDiffers: 'TotalDiffers',
    NumberDiffers: 'NumberDiffers',
    ContactDiffers: 'ContactDiffers',
    CurrencyDiffers: 'CurrencyDiffers',
    DeletedHere: 'DeletedHere',
    AmountDue: 'AmountDue',
    PaymentNotRead: 'PaymentNotRead',
    PaymentRemoved: 'PaymentRemoved',
    Wallet: 'Wallet',
    AllocationMissing: 'AllocationMissing',
    AllocationOpen: 'AllocationOpen',
    XeroOnly: 'XeroOnly',
    NoContact: 'NoContact',
    ContactMissing: 'ContactMissing',
    ContactArchived: 'ContactArchived',
} as const;

export type XeroSyncKind = (typeof XERO_SYNC_KIND)[keyof typeof XERO_SYNC_KIND];

/** Which findings the list shows. */
export const XERO_SYNC_STATE = { Open: 'Open', Dismissed: 'Dismissed' } as const;

export type XeroSyncState = (typeof XERO_SYNC_STATE)[keyof typeof XERO_SYNC_STATE];

export const XERO_SYNC_RUN_STATUS = { Waiting: 'Waiting', Done: 'Done', Failed: 'Failed' } as const;

export type XeroSyncRunStatus = (typeof XERO_SYNC_RUN_STATUS)[keyof typeof XERO_SYNC_RUN_STATUS];

export type XeroSyncTrigger = 'Nightly' | 'Requested';

/** A run of the check. `retryAt`: when a nightly run that failed tries again. */
export interface XeroSyncRun {
    id: number;
    trigger: XeroSyncTrigger;
    status: XeroSyncRunStatus;
    createdAt: string;
    startedAt: string | null;
    finishedAt: string | null;
    problem: string | null;
    found: number | null;
    new: number | null;
    retryAt: string | null;
}

/** What one side says: an amount in the finding's currency, words, or both. */
export interface XeroSyncSide {
    amount: number | null;
    text: string | null;
}

export interface XeroSyncDismissal {
    reason: string;
    by: string;
    at: string;
}

export interface XeroSyncFinding {
    id: number;
    kind: XeroSyncKind;
    check: XeroSyncCheck;
    order: { id: number; name: string } | null;
    client: { id: number; name: string } | null;
    /** The document's number: the admin's, or Xero's for one only Xero has. */
    document: string | null;
    currency: string | null;
    admin: XeroSyncSide;
    xero: XeroSyncSide;
    /** What explains the figures; it never decides anything. */
    detail: string | null;
    /** The document or contact in Xero, opened in the organisation the admin is connected to. */
    xeroUrl: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    /** Goes back with a dismissal, so one made on figures a later check changed is refused. */
    figures: string;
    dismissal: XeroSyncDismissal | null;
}

/**
 * The tab's answer: the latest run that ended, the latest that finished, and one under way, when
 * the next nightly run is, how many findings each check has open, and the findings asked for.
 */
export interface XeroSync {
    /** The latest run that ended, finished or failed. */
    lastRun: XeroSyncRun | null;
    /** The latest run that finished: when Xero was last checked, and what the list is from. */
    lastChecked: XeroSyncRun | null;
    running: XeroSyncRun | null;
    nextNightlyAt: string;
    open: Record<XeroSyncCheck, number>;
    dismissed: number;
    findings: XeroSyncFinding[];
    mayAct: boolean;
}

export interface XeroSyncParams {
    check?: XeroSyncCheck;
    state?: XeroSyncState;
}

const root = '/api/xero/sync';

export const xeroSyncApi = {
    get: ({ check, state }: XeroSyncParams) => {
        const search = new URLSearchParams();
        if (check) search.set('check', check);
        if (state && state !== XERO_SYNC_STATE.Open) search.set('state', state);
        const qs = search.toString();
        return api<XeroSync>(qs ? `${root}?${qs}` : root);
    },
    /** Check now: the run taken up straight away, or the one already waiting. */
    run: () => api<XeroSyncRun>(`${root}/runs`, { method: 'POST' }),
    dismiss: (id: number, reason: string, figures: string) =>
        api<XeroSyncFinding>(`${root}/findings/${id}/dismissal`, {
            method: 'POST',
            body: { reason, figures },
        }),
    restore: (id: number) =>
        api<XeroSyncFinding>(`${root}/findings/${id}/dismissal`, { method: 'DELETE' }),
};
